import { z } from 'zod';

/**
 * AI Model Category Enum
 *
 * Comprehensive list of AI model categories derived from provider APIs.
 * Stored as string in database for flexibility, validated at application layer.
 */
export const AI_MODEL_CATEGORIES = [
  '3d-to-3d',
  'audio-to-audio',
  'audio-to-video',
  'image-to-3d',
  'image-to-image',
  'image-to-json',
  'image-to-video',
  'json',
  'llm',
  'speech-to-speech',
  'speech-to-text',
  'text-to-3d',
  'text-to-image',
  'text-to-speech',
  'text-to-video',
  'training',
  'video-to-audio',
  'video-to-video',
  'vision',
] as const;

export const AiModelCategorySchema = z.enum(AI_MODEL_CATEGORIES);

export type AiModelCategory = z.infer<typeof AiModelCategorySchema>;

/**
 * Human-readable labels for categories
 * Used in admin panel and UI components
 */
export const AI_MODEL_CATEGORY_LABELS: Record<AiModelCategory, string> = {
  '3d-to-3d': '3D to 3D',
  'audio-to-audio': 'Audio to Audio',
  'audio-to-video': 'Audio to Video',
  'image-to-3d': 'Image to 3D',
  'image-to-image': 'Image to Image',
  'image-to-json': 'Image to JSON',
  'image-to-video': 'Image to Video',
  'json': 'JSON',
  'llm': 'Large Language Model',
  'speech-to-speech': 'Speech to Speech',
  'speech-to-text': 'Speech to Text',
  'text-to-3d': 'Text to 3D',
  'text-to-image': 'Text to Image',
  'text-to-speech': 'Text to Speech',
  'text-to-video': 'Text to Video',
  'training': 'Model Training',
  'video-to-audio': 'Video to Audio',
  'video-to-video': 'Video to Video',
  'vision': 'Computer Vision',
};

/**
 * Helper function to get category label
 */
export function getCategoryLabel(category: string): string {
  if (AiModelCategorySchema.safeParse(category).success) {
    return AI_MODEL_CATEGORY_LABELS[category as AiModelCategory];
  }
  return category;
}

/**
 * Helper function to validate category
 */
export function isValidCategory(category: string): category is AiModelCategory {
  return AiModelCategorySchema.safeParse(category).success;
}
